import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSDisclosuresData,
  type EnrollmentHYCSDisclosuresSubmitInput,
  type EnrollmentHYCSDisclosuresSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS Disclosures data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSDisclosuresData>
 * @example const response = await getEnrollmentHYCSDisclosuresData()
 */
export const getEnrollmentHYCSDisclosuresData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSDisclosuresData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSDisclosuresData',
      query: `mutation getEnrollmentHYCSDisclosuresData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSDisclosuresData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSDisclosuresData = createQueryHook<undefined, EnrollmentHYCSDisclosuresData>(
  getEnrollmentHYCSDisclosuresData,
)

/**
 * Submit Enrollment HYCS Disclosures data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSDisclosuresSubmitOutput>
 * @example const response = await submitEnrollmentHYCSDisclosuresData();
 */
export const submitEnrollmentHYCSDisclosuresData = async (
  enrollmentDisclosuresSubmitInput: EnrollmentHYCSDisclosuresSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSDisclosuresSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSDisclosuresData',
      query: `mutation submitEnrollmentHYCSDisclosuresData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDisclosuresSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSDisclosuresSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSDisclosuresData = createMutationHook<
  EnrollmentHYCSDisclosuresSubmitInput,
  EnrollmentHYCSDisclosuresSubmitOutput
>(submitEnrollmentHYCSDisclosuresData)
